
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  fetchOpenAIModels, 
  generateChatCompletion, 
  generateImage,
  ChatMessage
} from '@/services/agentic/openaiService';
import {
  fetchAnthropicModels,
  generateAnthropicCompletion
} from '@/services/agentic/anthropicService';
import { Loader2, Bot, Image } from 'lucide-react';
import { toast } from 'sonner';

// Define supported providers
type AIProvider = 'openai' | 'anthropic';

const AIPlatformTester = () => {
  const [provider, setProvider] = useState<AIProvider>('openai');
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
  const [prompt, setPrompt] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState('');
  const [tab, setTab] = useState('chat');
  const [isLoadingModels, setIsLoadingModels] = useState(true);

  // Load models based on selected provider
  useEffect(() => {
    const loadModels = async () => {
      setIsLoadingModels(true);
      setAvailableModels([]);
      
      try {
        let models = [];
        
        if (provider === 'openai') {
          models = await fetchOpenAIModels();
          if (models.length > 0) {
            setSelectedModel('gpt-4o-mini');
          }
        } else if (provider === 'anthropic') {
          models = await fetchAnthropicModels();
          if (models.length > 0) {
            setSelectedModel('claude-3-5-sonnet-latest');
          }
        }
        
        console.log(`Available ${provider} models:`, models);
        setAvailableModels(models);
      } catch (error) {
        console.error(`Error loading ${provider} models:`, error);
        toast.error(`Failed to load ${provider} models`);
      } finally {
        setIsLoadingModels(false);
      }
    };

    loadModels();
  }, [provider]);

  const handleChatGeneration = async () => {
    if (!prompt.trim()) return;

    setIsLoading(true);
    setResult('');

    try {
      if (provider === 'openai') {
        const messages: ChatMessage[] = [
          { role: 'user', content: prompt }
        ];

        const response = await generateChatCompletion(messages, {
          model: selectedModel,
          temperature: 0.7
        });

        setResult(response.choices[0].message.content);
      } else if (provider === 'anthropic') {
        const response = await generateAnthropicCompletion([
          { role: 'user', content: prompt }
        ], {
          model: selectedModel,
          temperature: 0.7
        });

        setResult(response.content[0].text);
      }
    } catch (error) {
      console.error('Error generating chat response:', error);
      setResult('Error generating response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageGeneration = async () => {
    if (!imagePrompt.trim()) return;

    setIsLoading(true);
    setGeneratedImageUrl('');

    try {
      // Currently only OpenAI supports image generation
      if (provider === 'openai') {
        const response = await generateImage(imagePrompt);
        setGeneratedImageUrl(response.data[0].url);
      } else {
        toast.error('Image generation is currently only supported with OpenAI');
      }
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getChatModels = () => {
    if (provider === 'openai') {
      return availableModels.filter(model => 
        model.id.includes('gpt-') && !model.id.includes('instruct')
      );
    } else if (provider === 'anthropic') {
      return availableModels.filter(model => 
        model.id.includes('claude-')
      );
    }
    return [];
  };

  // Disable image tab for non-OpenAI providers
  useEffect(() => {
    if (provider !== 'openai' && tab === 'image') {
      setTab('chat');
    }
  }, [provider, tab]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>AI Platform Tester</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 mb-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">AI Provider</label>
            <Select 
              value={provider} 
              onValueChange={(value: AIProvider) => setProvider(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an AI provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="chat">
              <Bot className="mr-2 h-4 w-4" />
              Chat Completion
            </TabsTrigger>
            <TabsTrigger value="image" disabled={provider !== 'openai'}>
              <Image className="mr-2 h-4 w-4" />
              Image Generation
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="chat" className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Model</label>
              <Select 
                value={selectedModel} 
                onValueChange={setSelectedModel}
                disabled={isLoadingModels}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingModels ? (
                    <div className="flex items-center justify-center p-2">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading models...
                    </div>
                  ) : (
                    getChatModels().map(model => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.id}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Prompt</label>
              <Textarea
                placeholder="Enter your prompt here..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
            
            <Button 
              onClick={handleChatGeneration} 
              disabled={isLoading || !prompt.trim()}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate Response'
              )}
            </Button>
            
            {result && (
              <div className="mt-4 space-y-2">
                <label className="text-sm font-medium">Result</label>
                <div className="rounded-md border border-gray-300 p-4 bg-background">
                  <pre className="whitespace-pre-wrap">{result}</pre>
                </div>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="image" className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Image Prompt</label>
              <Textarea
                placeholder="Describe the image you want to generate..."
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
            
            <Button 
              onClick={handleImageGeneration} 
              disabled={isLoading || !imagePrompt.trim()}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Image...
                </>
              ) : (
                'Generate Image'
              )}
            </Button>
            
            {generatedImageUrl && (
              <div className="mt-4 space-y-2">
                <label className="text-sm font-medium">Generated Image</label>
                <div className="rounded-md border border-gray-300 p-2 bg-background">
                  <img 
                    src={generatedImageUrl} 
                    alt="Generated" 
                    className="max-w-full rounded"
                  />
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AIPlatformTester;
